// YERİ: src/app/domains/site/sections/business.sections.ts
//
// TİCARİ SEKTÖREL BÖLÜMLER — danışmanlık paketleri, organizasyon paketleri,
// mekân listesi, sadakat kademeleri.
//
// Dördü de veriyi kiracının PORTAL kayıtlarından çeker; fiyat elle yazılmaz.
// Fiyatın iki yerde tutulması, birinin güncellenip diğerinin unutulması
// demektir — ve sitedeki eski fiyat kiracıyı bağlayan bir taahhüde döner.
//
// Danışmanlık ve organizasyon paketleri AYRI bölüm tipleri: aynı bileşene
// "kaynak" ayarı koymak, kiracının yanlış kaynağı seçip boş bölüm yayınlaması
// anlamına gelirdi. Her sektör yalnız kendi tipini görüyor.

import { Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { SiteConfig, cfg } from './section.model';
import { SiteChromeText } from '../core/site-chrome-text';
import { paraBirimi } from './community.sections';
import {
  TenantWebService, TenantPackage, TenantVenue, TenantLoyaltyTier,
} from '../core/tenant-web.service';

// ══════════════════════════════════════════════════════════════════════════
// PAKET IZGARASI (danışmanlık / organizasyon ortak gövdesi)
// ══════════════════════════════════════════════════════════════════════════

/**
 * İki paket bölümü aynı görünümü paylaşıyor; farkları yalnız hangi uçtan
 * beslendikleri. Görünüm burada tek yerde duruyor ki biri düzeltilip
 * diğeri unutulmasın.
 */
@Component({
  selector: 'sec-package-cards',
  standalone: true,
  template: `
    @if (paket().length) {
    <section [id]="anchor()" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-6xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <div class="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          @for (p of paket(); track p.id) {
            <div class="flex flex-col rounded-2xl border border-neutral-200 bg-white p-6 transition hover:shadow-lg dark:border-neutral-800 dark:bg-neutral-950">
              @if (p.type) {
                <span class="text-xs font-semibold uppercase tracking-wide" style="color:var(--site-accent)">{{ p.type }}</span>
              }
              <h3 class="mt-1 text-xl font-bold text-neutral-900 dark:text-white">{{ p.name }}</h3>

              <!-- Sadece null denetimi yetmez: 0 da geçer ve ücretsiz sanılır. -->
              @if (p.price != null && p.price > 0) {
                <p class="mt-4 text-3xl font-bold tabular-nums" style="color:var(--site-primary)">
                  {{ fiyat(p) }}
                  @if (p.billingPeriod) {
                    <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">/ {{ p.billingPeriod }}</span>
                  }
                </p>
              } @else {
                <!-- Fiyatı hiç basmamak "fiyatı yok" gibi okunuyordu; kart sessizce
                     eksik görünüyordu. Diğer katalog bölümleriyle aynı davranış. -->
                <p class="mt-4 text-sm text-neutral-500 dark:text-neutral-400">{{ metin.t('pr.askPrice') }}</p>
              }

              @if (p.description) {
                <p class="mt-3 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ p.description }}</p>
              }

              @if (p.sessionCount || p.durationDays) {
                <ul class="mt-4 space-y-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                  @if (p.sessionCount) { <li>· {{ p.sessionCount }} {{ metin.t('pk.sessions') }}</li> }
                  @if (p.durationDays) { <li>· {{ p.durationDays }} {{ metin.t('pk.days') }}</li> }
                </ul>
              }

              @if (ctaText()) {
                <a [href]="ctaUrl()" class="mt-auto pt-6">
                  <span class="block rounded-lg px-4 py-2.5 text-center text-sm font-semibold text-white transition hover:opacity-90"
                        style="background:var(--site-primary)">{{ ctaText() }}</span>
                </a>
              }
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class PackageCardsSection {
  protected metin = inject(SiteChromeText);

  config = input<SiteConfig>({});
  /** Veriyi çeken üst bölüm doldurur. */
  paket = input<TenantPackage[]>([]);
  anchor = input<string>('packages');

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title = computed(() => cfg(this.config(), 'title', 'Paketler'));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  protected ctaText = computed(() => cfg(this.config(), 'ctaText', ''));
  protected ctaUrl = computed(() => cfg(this.config(), 'ctaUrl', '#contact'));

  protected fiyat = (p: TenantPackage) => paraBirimi(p.price, p.currency, this.metin.dil(), this.metin.paraKodu());
}

// ── Danışmanlık paketleri ───────────────────────────────────────────────────

@Component({
  selector: 'sec-consulting-packages',
  standalone: true,
  imports: [PackageCardsSection],
  template: `<sec-package-cards [config]="config()" [paket]="paket()" anchor="packages" />`,
})
export class ConsultingPackagesSection implements OnInit {
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected paket = signal<TenantPackage[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    this.svc.getConsultingPackages(id).subscribe({
      next: (l) => this.paket.set(l),
      error: () => this.paket.set([]),
    });
  }
}

// ── Organizasyon paketleri ──────────────────────────────────────────────────

@Component({
  selector: 'sec-event-packages',
  standalone: true,
  imports: [PackageCardsSection],
  template: `<sec-package-cards [config]="config()" [paket]="paket()" anchor="packages" />`,
})
export class EventPackagesSection implements OnInit {
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected paket = signal<TenantPackage[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    this.svc.getEventPackages(id).subscribe({
      next: (l) => this.paket.set(l),
      error: () => this.paket.set([]),
    });
  }
}

// ══════════════════════════════════════════════════════════════════════════
// MEKÂN LİSTESİ
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-venue-grid',
  standalone: true,
  template: `
    @if (mekan().length) {
    <section id="venues" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-6xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <div class="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          @for (m of mekan(); track m.id) {
            <div class="flex flex-col rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900">
              <h3 class="text-lg font-bold text-neutral-900 dark:text-white">{{ m.name }}</h3>
              @if (m.city) {
                <p class="mt-0.5 text-sm font-medium" style="color:var(--site-accent)">{{ m.city }}</p>
              }
              @if (m.address) {
                <p class="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ m.address }}</p>
              }
              <div class="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                @if (m.capacity) {
                  <span class="text-sm text-neutral-600 dark:text-neutral-400">{{ metin.t('vn.capacity') }}: <strong class="tabular-nums">{{ m.capacity }}</strong></span>
                }
                @if (m.dailyRate != null) {
                  <span class="text-sm font-bold tabular-nums" style="color:var(--site-primary)">{{ ucret(m) }} <span class="font-medium text-neutral-500 dark:text-neutral-400">{{ metin.t('vn.perDay') }}</span></span>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class VenueGridSection implements OnInit {
  protected metin = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected mekan = signal<TenantVenue[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    const adet = Number(cfg(this.config(), 'count', 6)) || 6;
    this.svc.getVenues(id, adet).subscribe({
      next: (l) => this.mekan.set(l),
      error: () => this.mekan.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title = computed(() => cfg(this.config(), 'title', 'Mekânlarımız'));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected ucret = (m: TenantVenue) => paraBirimi(m.dailyRate, m.currency, this.metin.dil(), this.metin.paraKodu());
}

// ══════════════════════════════════════════════════════════════════════════
// SADAKAT KADEMELERİ
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-loyalty-tiers',
  standalone: true,
  template: `
    @if (kademe().length) {
    <section id="loyalty" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-5xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <div class="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          @for (k of kademe(); track k.id) {
            <div class="relative overflow-hidden rounded-2xl border border-neutral-200 bg-white p-6 text-center dark:border-neutral-800 dark:bg-neutral-950">
              <!-- Kademe rengi kiracının kendi seçimi; girilmemişse tema
                   vurgusuna düşer, böylece şerit hiçbir zaman kaybolmaz. -->
              <span class="absolute inset-x-0 top-0 h-1.5" [style.background]="renk(k)"></span>
              <h3 class="text-lg font-bold text-neutral-900 dark:text-white">{{ k.name }}</h3>
              <p class="mt-4 text-3xl font-bold tabular-nums" [style.color]="renk(k)">%{{ k.discountPercent }}</p>
              <p class="mt-1 text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{{ metin.t('ly.discount') }}</p>
              <p class="mt-4 text-sm text-neutral-600 dark:text-neutral-400">
                {{ metin.t('ly.from') }} <strong class="tabular-nums">{{ k.minPoints }}</strong> {{ metin.t('ly.points') }}
              </p>
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class LoyaltyTiersSection implements OnInit {
  protected metin = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected kademe = signal<TenantLoyaltyTier[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;
    this.svc.getLoyaltyTiers(id).subscribe({
      next: (l) => this.kademe.set(l),
      error: () => this.kademe.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title = computed(() => cfg(this.config(), 'title', 'Sadakat Programı'));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected renk = (k: TenantLoyaltyTier) => k.badgeColor || 'var(--site-accent)';
}
